import { useEffect, useRef, type FC } from 'react'
import { Box } from '@mui/material'

interface AddSubPipeChipProps {
  label: string
  isDark: boolean
  onStart: () => void
}

/**
 * The "+ Add Sub Pipe" strip that sits on the boundary under a pipe row.
 *
 * `mousedown` is stopped with a NATIVE listener as well as React's own: the
 * grid opens a cell editor from the cell's click handler, and the strip floats
 * over that cell. Swallowing the event at the source keeps the click from ever
 * reaching the row underneath.
 */
export const AddSubPipeChip: FC<AddSubPipeChipProps> = ({
  label,
  isDark,
  onStart,
}) => {
  const ref = useRef<HTMLSpanElement | null>(null)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    const swallow = (event: MouseEvent) => {
      event.preventDefault()
      event.stopPropagation()
    }
    el.addEventListener('mousedown', swallow)
    return () => el.removeEventListener('mousedown', swallow)
  }, [])

  const accent = isDark ? '#5fe0ad' : '#004744'

  return (
    <Box
      ref={ref}
      component="span"
      data-no-cell-edit=""
      onClick={(event: React.MouseEvent) => {
        event.stopPropagation()
        onStart()
      }}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        height: '20px',
        padding: '0 10px',
        borderRadius: '9px',
        border: `1px solid ${accent}`,
        backgroundColor: isDark ? '#0d2a38' : '#ffffff',
        color: accent,
        fontSize: '10px',
        fontWeight: 700,
        letterSpacing: '0.4px',
        lineHeight: 1,
        whiteSpace: 'nowrap',
        userSelect: 'none',
        cursor: 'pointer',
        boxShadow: isDark
          ? '0 2px 6px rgba(0, 0, 0, 0.55)'
          : '0 2px 6px rgba(16, 24, 40, 0.18)',
      }}
    >
      {label}
    </Box>
  )
}

/** The dashed "+ Add" affordance a create row shows before it is typed into. */
export const AddRowChip: FC<{
  label: string
  disabled: boolean
  isDark: boolean
  onClick: () => void
}> = ({ label, disabled, isDark, onClick }) => (
  <Box
    component="span"
    data-no-cell-edit
    onClick={disabled ? undefined : onClick}
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      padding: '2px 12px',
      height: '36px',
      borderRadius: '8px',
      border: disabled
        ? '1px dashed #e0e0e0'
        : `1px dashed ${isDark ? 'rgba(52, 211, 153, 0.5)' : 'rgba(0, 71, 68, 0.5)'}`,
      cursor: disabled ? 'not-allowed' : 'pointer',
      fontSize: '12px',
      fontWeight: 500,
      color: disabled ? '#bdbdbd' : isDark ? '#5fe0ad' : '#004744',
    }}
  >
    {label}
  </Box>
)
